<template>
  <div class="container py-4">
    <div class="row justify-content-center">
      <div class="col-md-8">

        <!-- Top bar -->
        <div class="page-bar py-3">
            <div class="d-flex align-items-center">
            <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
              <i class="fas fa-chevron-left"></i>
            </BackButton>
            <div class="page-title flex-grow-1">
              {{ title || 'Event' }}
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center p-5">
          <div class="spinner-border text-primary" role="status"></div>
          <div class="mt-2 text-muted small">Loading event details…</div>
        </div>

        <!-- Content -->
        <div v-else class="container mt-3 mb-5 shadow-sm bg-white py-3 rounded">

          <!-- Carousel -->
          <div class="hero my-2" v-if="photosClean.length">
            <div class="slide-frame" @mouseenter="pauseAuto" @mouseleave="resumeAuto">
              <div
                class="slide-bg-blur"
                :style="{ backgroundImage: `url(${photosClean[currentSlide]})` }"
              />
              <img
                :key="`slide-${currentSlide}`"
                :src="photosClean[currentSlide]"
                class="slide-img fit-contain"
                :alt="`Slide ${currentSlide + 1}`"
                @load="onImgLoad"
              />
              <button v-if="photosClean.length > 1" class="nav prev" @click="prevSlide" aria-label="Previous">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button v-if="photosClean.length > 1" class="nav next" @click="nextSlide" aria-label="Next">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>

            <div class="thumbs mt-2" v-if="photosClean.length > 1">
              <button
                v-for="(src, i) in photosClean"
                :key="`thumb-${i}`"
                class="thumb-btn"
                :class="{ active: i === currentSlide }"
                @click="goToSlide(i)"
                :title="`Go to image ${i+1}`"
              >
                <img v-no-right-click :src="src" class="thumb" alt="thumbnail" />
              </button>
            </div>
          </div>

          <!-- Title -->
          <h5 class="mb-2">{{ title || 'Event' }}</h5>

          <!-- About -->
          <div class="mb-3" v-if="info.description">
            <div class="section-title">About The Event</div>
            <p class="mb-0">{{ info.description }}</p>
          </div>

          <!-- Event details -->
          <div class="mb-3">
            <div class="icon-row d-flex align-items-start text-small mb-2">
              <span class="emoji-icon mt-1 mr-2" aria-hidden="true">📅</span>
              <div>{{ eventDateRange }}</div>
            </div>

            <div class="icon-row d-flex align-items-start text-small mb-2">
              <span class="emoji-icon mt-1 mr-2" aria-hidden="true">📍</span>
              <div class="flex-grow-1 text-dark">
                {{ info.fulladdress || '—' }}
              </div>
              <a
                v-if="hasMap"
                :href="mapHref"
                target="_blank"
                rel="noopener"
                class="ml-2 link-arrow"
                title="Open in Maps"
              >
                <i class="fas fa-location-arrow"></i>
              </a>
            </div>

            <div v-if="hasMap" class="map-card mt-3">
              <div class="map-card__header">
                <span><i class="fas fa-map-marked-alt mr-2"></i>Event Location</span>
                <a :href="mapHref" target="_blank" rel="noopener">Open in Google Maps</a>
              </div>
              <div class="map-frame">
                <iframe
                  class="map-frame__iframe"
                  :src="mapEmbedSrc"
                  loading="lazy"
                  allowfullscreen
                  referrerpolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>

          <div class="mb-3 text-small">
            Host: <strong>{{ info.hostname || '-' }}</strong>
          </div>

          <div class="mb-3">
            <div class="section-title">Add to Calendar</div>
            <AddToCalendar
              :title="title || 'Event'"
              :description="info.description || ''"
              :location="info.address || ''"
              :start="eventStart"
              :end="eventEnd"
              :url="eventUrl"
            />
          </div>

          <hr>

          <!-- Primary RSVP buttons (first-time only) -->
          <div v-if="showPrimaryButtons" class="pt-3">
            <div class="btn-group w-100 btn-group-sm">
              <button type="button" class="btn btn-primary w-100 m-1" @click="openRsvpModal(eventRow, 'new', 1)">YES</button>
              <button type="button" class="btn btn-light   w-100 m-1" @click="openRsvpModal(eventRow, 'new', 2)">NO</button>
              <button type="button" class="btn btn-info    w-100 m-1" @click="openRsvpModal(eventRow, 'new', 3)">MAY</button>
            </div>
          </div>

          <!-- If RSVP is completed/locked but we want to show what it was -->
          <div class="pt-3 row" v-else-if="Number(eventRow.complete_status) === 1">
            <div class="col-12">
              <p class="mb-0 small text-success" v-if="Number(eventRow.status) === 1">
                <i class="fas fa-check-double mr-1"></i>
                <span v-if="rsvpFamilyEnabled">YES • ADULTS : {{ eventRow.adult }}, CHILDREN: {{ eventRow.child }}</span>
                <span v-else>YES</span>
              </p>
              <p class="mb-0 small" style="color:red;" v-else-if="Number(eventRow.status) === 2">
                <i class="fas fa-check-double mr-1"></i>
                <span v-if="rsvpFamilyEnabled">NO • ADULTS : {{ eventRow.adult }}, CHILDREN: {{ eventRow.child }}</span>
                <span v-else>NO</span>
              </p>
              <p class="mb-0 small text-primary" v-else-if="Number(eventRow.status) === 3">
                <i class="fas fa-check-double mr-1"></i>
                <span v-if="rsvpFamilyEnabled">MAY BE • ADULTS : {{ eventRow.adult }}, CHILDREN: {{ eventRow.child }}</span>
                <span v-else>MAY BE</span>
              </p>
              <p class="mb-0 small text-primary" v-else>
                <i class="fas fa-check-double mr-1"></i>you did not Respond
              </p>
            </div>
          </div>

          <!-- After RSVP submitted (can update) -->
          <div class="pt-3 row" v-else-if="rsvpEnabled"> 
            <div class="col-12 text-right my-3">
              <button
                type="button"
                v-if="[1,2,3].includes(Number(eventRow.status)) && Number(eventRow.complete_status) !== 1"
                class="btn btn-primary btn-sm px-3 py-1"
                @click="openRsvpModal(eventRow, 'update', Number(eventRow.status))"
              >
                Update RSVP
              </button>
            </div>
          </div>

          <!-- RSVP summary -->
          <div class="mb-3" v-if="rsvpEnabled">
            <div class="section-title">RSVP</div>
            <span v-if="Number(eventRow.status) === 1" class="badge badge-success">
              <span v-if="rsvpFamilyEnabled">Confirmed • Adults: {{ eventRow.adult || 0 }}, Child: {{ eventRow.child || 0 }}</span>
              <span v-else>Confirmed</span>
            </span>
            <span v-else-if="Number(eventRow.status) === 2" class="badge badge-danger">
              <span v-if="rsvpFamilyEnabled">Not Attending • Adults: 0, Child: 0</span>
              <span v-else>Not Attending</span>
            </span>
            <span v-else-if="Number(eventRow.status) === 3" class="badge badge-primary">
              <span v-if="rsvpFamilyEnabled">Maybe • Adults: {{ eventRow.adult || 0 }}, Child: {{ eventRow.child || 0 }}</span>
              <span v-else>Maybe</span>
            </span>
            <span v-else class="badge badge-secondary">No response</span>
          </div>

          <hr>

          <!-- Albums -->
          <div class="mb-2">
            <div class="section-title">Albums</div>
            <p class="text-small text-muted mb-2">
              When photos are shared for this event, you will be able to view those from here.
            </p>
            <router-link
              v-if="Number(info.album_photos_exist) === 1"
              :to="`/album/${info.eventid || eventId}?source=shared`"
              class="btn btn-primary w-100"
            >
              View Album
            </router-link>
            <button v-else class="btn btn-primary w-100" disabled>View Album</button>
          </div>
          <hr>

          <!-- Friends / Connections -->
          <div class="mb-3">
            <div class="section-title d-flex align-items-center">
              <i class="fas fa-user-friends mr-2 text-primary"></i>
              Common Connections Invited
            </div>

            <div v-if="showConnectionsSkeleton" class="connections-panel">
              <div class="connection-list" role="list" aria-label="Loading connections">
                <article v-for="n in 3" :key="`skeleton-${n}`" class="connection-item is-skeleton">
                  <div class="connection-avatar-wrap">
                    <div class="connection-avatar skeleton-box"></div>
                  </div>
                  <div class="connection-body">
                    <div class="connection-header">
                      <div class="connection-header-text">
                        <div class="skeleton-box skeleton-line w-60"></div>
                        <div class="skeleton-box skeleton-line w-40 mt-1"></div>
                      </div>
                      <div class="connection-chip skeleton-box w-30"></div>
                    </div>
                    <div class="skeleton-box skeleton-line w-90 mt-2"></div>
                  </div>
                </article>
              </div>
            </div>

            <div v-else class="connections-panel">
              <div class="connection-filters" role="toolbar" aria-label="Filter connections">
                <button
                  v-for="filter in connectionFilterOptions"
                  :key="filter.value"
                  type="button"
                  class="connection-filter-chip"
                  :class="{ active: connectionFilter === filter.value }"
                  :aria-pressed="String(connectionFilter === filter.value)"
                  @click="connectionFilter = filter.value"
                >
                  {{ filter.label }}
                </button>
              </div>
              <div class="connections-panel-body" :class="{ 'is-loading': connectionsLoading }">
                <div class="connections-overlay" v-if="connectionsLoading">
                  <div class="loading-bar"></div>
                </div>
                <template v-if="filteredConnections.length">
                  <div class="connection-list" role="list">
                    <article
                      v-for="(friend, idx) in visibleConnections"
                      :key="friend.id || friend.email || friend.phone || idx"
                      class="connection-item connection-item--clickable"
                      role="listitem"
                      @click="openConnectionEvents(friend)"
                    >
                      <div class="connection-avatar-wrap">
                        <img
                          v-no-right-click
                          :src="friend.user_img || defaultFriendAvatar"
                          :alt="friend.name || 'Connection'"
                          class="connection-avatar"
                          loading="lazy"
                        >
                      </div>
                      <div class="connection-body">
                        <div class="connection-main">
                          <div class="connection-name">{{ friend.name || 'Connection' }}</div>
                          <div v-if="friend.past_count_msg" class="connection-past-pill">
                            <i class="fas fa-users mr-1"></i>
                            <span>{{ friend.past_count_msg }}</span>
                          </div>
                        </div>
                        <span class="connection-chip" :class="connectionStateModifier(friend)">
                          {{ connectionBadgeLabel(friend) }}
                        </span>
                      </div>
                    </article>
                  </div>
                  <button
                    v-if="hiddenConnections.length"
                    type="button"
                    class="btn btn-outline-primary btn-sm mt-2"
                    @click="goToConnectionsPage"
                  >
                    +{{ hiddenConnections.length }} more
                  </button>
                </template>
                <div v-else class="connection-filter-empty" aria-live="polite">
                  <img :src="connectionsEmptyImage" alt="No connections for this filter" loading="lazy" /> 
                </div>
              </div>
            </div>
          </div>
          <hr>

          <!-- Feedback -->
          <div class="mb-2" v-if="showFeedbackSection">
            <div class="section-title">Feedback</div>

            <div class="d-flex align-items-center justify-content-between mb-2">
              <div class="text-small text-muted">
                Your feedback helps us improve.
              </div>

              <!-- if feedback already exists allow editing, otherwise show "Give feedback" -->
              <div>
                <button
                  class="btn btn-sm"
                  :class="Number(info.feedback_status) === 1 ? 'btn-outline-primary' : 'btn-primary'"
                  @click="openFeedback(info)"
                >
                  <i :class="Number(info.feedback_status) === 1 ? 'fas fa-edit' : 'fas fa-comment-alt'"></i>
                  <span class="ml-1">
                    {{ Number(info.feedback_status) === 1 ? 'Update feedback' : 'Give feedback' }}
                  </span>
                </button>
              </div>
            </div>

            <div v-if="lastFeedbackRating || info.feedback_msg" class="feedback-summary mt-2">
              <div class="feedback-chip" v-if="lastFeedbackRating">
                <i class="fas fa-star mr-1"></i>
                <span>{{ lastFeedbackRating }}/5</span>
              </div>
              <p v-if="info.feedback_msg" class="mb-0 feedback-summary__text">{{ info.feedback_msg }}</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>


    <!-- RSVP Modal (centered) -->
    <div v-if="rsvpOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-card rsvp-modal" role="dialog" aria-modal="true" aria-labelledby="rsvpTitle">
        <div class="modal-header">
          <h5 id="rsvpTitle" class="mb-0">
            {{ modalMode === 'new' ? 'Invitation Confirmation' : 'Invitation Confirmation' }}
          </h5>
          <button class="close-btn" @click="closeModal" aria-label="Close">&times;</button>
        </div>

        <div class="modal-body rsvp-body">
          <div class="rsvp-meta" v-if="info.hostname || info.username">
            <div class="rsvp-meta__name">{{ info.hostname || 'Host' }}</div>
            <div class="rsvp-meta__sub" v-if="info.username">@{{ info.username }}</div>
          </div>

          <!-- For update mode: status switcher -->
          <div class="rsvp-choice-row" v-if="rsvpEnabled">
            <button
              type="button"
              class="rsvp-chip yes"
              :class="{ active: modalStatus === 1 }"
              @click="selectStatus(1)"
            >
              Yes
            </button>
            <button
              type="button"
              class="rsvp-chip no"
              :class="{ active: modalStatus === 2 }"
              @click="selectStatus(2)"
            >
              No
            </button>
            <button
              type="button"
              class="rsvp-chip maybe"
              :class="{ active: modalStatus === 3 }"
              @click="selectStatus(3)"
            >
              Maybe
            </button>
          </div>

          <!-- Counts -->
          <div class="rsvp-grid" v-if="rsvpFamilyEnabled">
            <div class="rsvp-counter">
              <label :for="`rsvp-adults-${modalId || 'new'}`">Adults</label>
              <div class="stepper" :class="{ disabled: modalStatus === 2 }">
                <button
                  type="button"
                  class="step-btn"
                  :disabled="modalStatus === 2 || modalAdult <= 0"
                  @click="changeCount('adult', -1)"
                >−</button>
                <div class="step-value" :aria-live="modalStatus === 2 ? 'off' : 'polite'">{{ modalAdult }}</div>
                <button
                  type="button"
                  class="step-btn"
                  :disabled="modalStatus === 2"
                  @click="changeCount('adult', 1)"
                >+</button>
              </div>
            </div>
            <div class="rsvp-counter">
              <label :for="`rsvp-children-${modalId || 'new'}`">Children</label>
              <div class="stepper" :class="{ disabled: modalStatus === 2 }">
                <button
                  type="button"
                  class="step-btn"
                  :disabled="modalStatus === 2 || modalChild <= 0"
                  @click="changeCount('child', -1)"
                >−</button>
                <div class="step-value" :aria-live="modalStatus === 2 ? 'off' : 'polite'">{{ modalChild }}</div>
                <button
                  type="button"
                  class="step-btn"
                  :disabled="modalStatus === 2"
                  @click="changeCount('child', 1)"
                >+</button>
              </div>
            </div>
          </div>

          <p v-if="modalStatus === 2 && rsvpFamilyEnabled" class="rsvp-note">
            When choosing <strong>No</strong>, adults and children are set to 0.
          </p>

          <div class="form-group mb-0">
            <label class="small" :for="`detail-rsvp-notes-${modalId || 'new'}`">Notes (optional)</label>
            <textarea
              :id="`detail-rsvp-notes-${modalId || 'new'}`"
              class="form-control"
              rows="3"
              v-model.trim="modalNotes"
              placeholder="Add any notes for the host"
            ></textarea>
          </div>
        </div>

        <div class="modal-footer rsvp-actions">
          <button class="btn btn-light rsvp-btn" @click="closeModal">Cancel</button>
          <button class="btn btn-primary rsvp-btn primary" :disabled="saving" @click="saveRsvp">
            <span v-if="!saving">Save</span>
            <span v-else class="spinner-border spinner-border-sm">Saving</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Feedback modal (create/update feedback) -->
    <div v-if="feedbackOpen" class="modal-overlay" @click.self="closeFeedback">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="feedbackTitle">
        <div class="modal-header">
          <h5 id="feedbackTitle" class="mb-0">
            {{ feedbackId ? 'Update Feedback' : 'Give Feedback' }}
          </h5>
          <button class="close-btn" @click="closeFeedback" aria-label="Close">&times;</button>
        </div>

        <div class="modal-body">
          <div class="d-flex align-items-center mb-3">
            <img v-no-right-click :src="info.eventphoto || info.p1_url || placeholderImage" class="rounded mr-3" style="width:72px;height:72px;object-fit:cover" alt="event"/>
            <div>
              <div style="font-weight:700">{{ info.orgname || info.eventname || 'Event' }}</div>
              <div class="small text-muted">Host: {{ info.hostname || '-' }}</div>
            </div>
          </div>

          <div class="mb-3" v-if="showRatingPicker">
            <div class="small text-muted mb-2">How are you feeling about this event?</div>
            <div class="feedback-scale">
              <FeedbackReaction
                v-model="feedbackRating"
                :labels="feedbackLabels"
                :emoji-width="feedbackEmojiSize"
                :emoji-height="feedbackEmojiSize"
              /> 
            </div>
          </div>

          <div class="form-group mb-2">
            <label class="small">Your feedback</label>
            <textarea
              v-model="feedbackMsg"
              class="form-control"
              rows="4"
              placeholder="Write your feedback..."
              @input="feedbackError = ''"
            ></textarea>
            <div v-if="feedbackError" class="text-danger small mt-1">{{ feedbackError }}</div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-outline-secondary" @click="closeFeedback" :disabled="savingFeedback">Cancel</button>
          <button class="btn btn-primary" @click="submitFeedback" :disabled="savingFeedback">
            <span v-if="!savingFeedback">Save Feedback</span>
            <span v-else class="spinner-border spinner-border-sm">Saving</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Connection events modal -->
    <div v-if="connectionEventsOpen" class="modal-overlay" @click.self="closeConnectionEvents">
      <div class="modal-card connections-modal" role="dialog" aria-modal="true" aria-labelledby="connectionEventsTitle">
        <div class="modal-header d-flex align-items-center">
          <div class="d-flex align-items-center">
            <img
              v-no-right-click
              :src="selectedConnection?.user_img || defaultFriendAvatar"
              alt="Connection avatar"
              class="modal-avatar mr-2"
            >
            <div>
              <h5 id="connectionEventsTitle" class="mb-0">{{ selectedConnection?.name || 'Connection' }}</h5> 
            </div>
          </div>
          <button class="close-btn ml-auto" @click="closeConnectionEvents" aria-label="Close">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="connectionEventsLoading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status"></div>
            <div class="mt-2 small text-muted">Loading events...</div>
          </div>
          <div v-else-if="connectionEventsError" class="alert alert-warning mb-0">
            {{ connectionEventsError }}
          </div>
          <div v-else-if="connectionEvents.length">
            <router-link
              v-for="event in connectionEvents"
              :key="event.id"
              class="event-card"
              :to="event.eventId ? `/received/${event.eventId}` : undefined"
              @click="event.eventId && closeConnectionEvents()"
            >
              <div class="event-card__media" v-if="event.image">
                <img :src="event.image" alt="Event" class="event-img" loading="lazy">
              </div>
              <div class="event-card__body">
                <div class="event-title">{{ event.title }}</div>
                <div class="event-meta text-muted small">
                  <span class="emoji-icon mr-1" aria-hidden="true">📅</span>{{ event.dateRange || 'Date N/A' }}
                </div>
                <div class="event-meta text-muted small" v-if="event.location">
                  <span class="emoji-icon mr-1" aria-hidden="true">📍</span>{{ event.location }}
                </div>
                <div class="event-meta text-muted small" v-if="event.host">
                  <i class="fas fa-user mr-1"></i>{{ event.host }}
                </div>
                <p v-if="event.description" class="mb-0 text-small text-muted mt-2">{{ event.description }}</p>
              </div>
            </router-link>
          </div>
          <div v-else class="text-center text-muted small py-3">
            No events found for this connection.
          </div>
        </div>
      </div>
    </div>

</template>

<script setup>
/* eslint-disable */ 
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api' 
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import { formatEventDateRange } from '@/utils/date'
import BackButton from '@/components/common/BackButton.vue'
import AddToCalendar from '@/components/common/AddToCalendar.vue'
import FeedbackReaction from '@/components/common/FeedbackReaction.vue'

/* emits (modal save expects to notify parent if needed) */
const emit = defineEmits(['rsvp-updated'])

/* ---------- route / ids ---------- */
const route = useRoute()
const router = useRouter()
const session = useSessionStore()

// Accept either /:eventid or /:id
const deriveEventId = (r) => {
  const cand = [r.params?.eventid, r.params?.id, r.query?.eventid, r.query?.id]
    .find(v => v != null && String(v).trim() !== '')
  if (cand) return String(cand)
  const lastSeg = (r.path || r.fullPath || '').split('?')[0].split('/').filter(Boolean).pop()
  return lastSeg && /^\d+$/.test(lastSeg) ? lastSeg : null
}
const eventId = ref(deriveEventId(route))
watch(() => route.fullPath, () => (eventId.value = deriveEventId(route)))

/* ---------- state ---------- */
const loading = ref(true)
const eventRow = ref({})   // API list[0]
const info = ref({})       // same row alias
const connections = ref([])
const connectionsLoading = ref(false)
const defaultFriendAvatar = '/assets/img/av.png'
const defaultEventImage = 'https://dummyimage.com/400x240/e9edf5/7c8697.png&text=Event'
const connectionsEmptyImage = '/assets/icons/no-connections.svg'
const MAX_VISIBLE_CONNECTIONS = 4
const defaultConnectionFilters = [
  { value: '', label: 'All' },
  { value: '1', label: 'Going' },
  { value: '3', label: 'Not Decided' },
  { value: '2', label: 'Not Going' },
  { value: '0', label: 'Invited' }
]
const connectionFilterOptions = ref([...defaultConnectionFilters])
const connectionFilter = ref(connectionFilterOptions.value[0].value)

const numberOrZero = (value) => {
  const parsed = Number(value)
  return Number.isNaN(parsed) ? 0 : parsed
}
const normalizeFlag = (val) => {
  const s = String(val ?? '').trim().toLowerCase()
  if (s === '1' || s === 'yes' || s === 'true') return true
  return Number(s) === 1
}

/* ---------- formatting ---------- */
const eventDateRange = computed(() => {
  const text = formatEventDateRange({
    fromDate: info.value.fromdate,
    toDate: info.value.todate,
    fromTime: info.value.fromtime,
    toTime: info.value.totime
  })
  return text || ''
})
const hasMap = computed(() => Boolean((info.value.lat || info.value.latitude) && (info.value.lng || info.value.longitude)))
const mapHref = computed(() => {
  if (!hasMap.value) return '#'
  const lat = info.value.lat || info.value.latitude
  const lng = info.value.lng || info.value.longitude
  return `https://www.google.com/maps?q=${encodeURIComponent(lat)},${encodeURIComponent(lng)}`
})
const mapEmbedSrc = computed(() => {
  if (!hasMap.value) return ''
  const lat = info.value.lat || info.value.latitude
  const lng = info.value.lng || info.value.longitude
  return `https://www.google.com/maps?q=${encodeURIComponent(lat)},${encodeURIComponent(lng)}&hl=en&output=embed`
})
const eventStart = computed(() => buildDateTime(info.value.fromdate, info.value.fromtime))
const eventEnd = computed(() => buildDateTime(info.value.todate || info.value.fromdate, info.value.totime || info.value.fromtime))
const eventUrl = computed(() => {
  if (typeof window === 'undefined') return ''
  return window.location.href
})
const rsvpEnabled = computed(() => normalizeFlag(info.value.rsvp_option))
const rsvpFamilyEnabled = computed(() => normalizeFlag(info.value.rsvp_family))

const filteredConnections = computed(() => {
  if (!connectionFilter.value && connectionFilter.value !== 0) return connections.value
  const map = { '1': 'going', '2': 'notgoing', '3': 'maybe', '0': 'pending' }
  const mapped = map[String(connectionFilter.value)] || null
  if (!mapped) return connections.value
  return connections.value.filter(friend => connectionState(friend) === mapped)
})
const visibleConnections = computed(() => filteredConnections.value.slice(0, MAX_VISIBLE_CONNECTIONS))
const hiddenConnections = computed(() => filteredConnections.value.slice(MAX_VISIBLE_CONNECTIONS))
const connectionsText = computed(() => `You have ${connections.value.length} mutual connections going to this event`)
const showConnectionsSkeleton = computed(() => connectionsLoading.value && !connections.value.length)

const connectionStatusLabel = (status) => {
  if (Number(status) === 1) return 'Going'
  if (Number(status) === 2) return 'Not going'
  if (Number(status) === 3) return 'Not Decided'
  return 'Invited'
}

const connectionBadgeLabel = (friend) =>
  Number(friend?.rsvp_status) === 1 ? connectionStatusLabel(friend?.status) : 'Invited'

const connectionState = (friend) => {
  if (Number(friend?.rsvp_status) !== 1) return 'pending'
  if (Number(friend?.status) === 1) return 'going'
  if (Number(friend?.status) === 2) return 'notgoing'
  if (Number(friend?.status) === 3) return 'maybe'
  return 'pending'
}

const connectionStateModifier = (friend) => `is-${connectionState(friend)}`

const lastFeedbackRating = computed(() => {
  const r = Number(info.value.feedback_rating || info.value.rating || 0)
  return r > 0 ? r : null
})
const showFeedbackSection = computed(() => Number(info.value?.feedback) === 1)

const updateEmojiSize = () => {
  if (typeof window === 'undefined') {
    feedbackEmojiSize.value = 54
    return
  }
  const w = window.innerWidth || 0
  if (w < 576) feedbackEmojiSize.value = 44
  else if (w < 768) feedbackEmojiSize.value = 50
  else feedbackEmojiSize.value = 54
}

const connectionMetaLine = (friend) => {
  const responded = Number(friend?.rsvp_status) === 1
  const base = responded ? `RSVP: ${connectionStatusLabel(friend?.status)}` : 'Invited · Waiting response'
  const extras = []

  if (friend?.mutual_friends) {
    extras.push(`Mutual: ${friend.mutual_friends} ${friend.mutual_friends === 1 ? 'friend' : 'friends'}`)
  } else if (friend?.mutual_events) {
    extras.push(`Events: ${friend.mutual_events}`)
  }

  return extras.length ? `${base} • ${extras.join(' • ')}` : base
}

const connectionDetailLine = (friend) => {
  if (!friend) return ''
  if (friend.response_note) return `Responded: "${friend.response_note}"`
  if (friend.note) return friend.note
  if (friend.last_seen) return friend.last_seen
  if (friend.source) return `Connected via ${friend.source}`
  return ''
}

const buildDateTime = (date, time) => {
  if (!date) return null
  const t = time && String(time).trim() ? time : '00:00'
  const d = new Date(`${date}T${t}`)
  return isNaN(d) ? null : d
}

/* ---------- photos / carousel ---------- */
const photosClean = computed(() => {
  const arr = Array.isArray(info.value.photos) ? info.value.photos : []
  return arr.map(s => String(s || '').trim()).filter(Boolean)
})
const currentSlide = ref(0)
let autoTimer = null
const AUTOPLAY_MS = 4000

const nextSlide = () => {
  const n = photosClean.value.length
  if (!n) return
  currentSlide.value = (currentSlide.value + 1) % n
}
const prevSlide = () => {
  const n = photosClean.value.length
  if (!n) return
  currentSlide.value = (currentSlide.value - 1 + n) % n
}
const goToSlide = (i) => {
  const n = photosClean.value.length
  if (!n) return
  currentSlide.value = Math.max(0, Math.min(i, n - 1))
}
const startAuto = () => {
  stopAuto()
  if (photosClean.value.length > 1) {
    autoTimer = setInterval(nextSlide, AUTOPLAY_MS)
  }
}
const stopAuto = () => {
  if (autoTimer) {
    clearInterval(autoTimer)
    autoTimer = null
  }
}
const pauseAuto = stopAuto
const resumeAuto = () => { if (photosClean.value.length > 1) startAuto() }
watch(photosClean, () => { currentSlide.value = 0; stopAuto(); if (photosClean.value.length > 1) startAuto() })

// placeholder for img load (no-op but safe)
function onImgLoad(){}

/* ---------- title ---------- */
const title = computed(() => info.value.orgname || info.value.eventname || '')

/* ---------- derived UI flags (based on API payload) ---------- */
const showPrimaryButtons = computed(() => {
  if (!rsvpEnabled.value) return false
  // First-time answer if status == 0. Keep the original nuance around active_status.
  const s = Number(eventRow.value.status || 0)
  const active = Number(eventRow.value.active_status || 0)
  if (s !== 0) return false
  if (active === 1) return true
  if (active === 0 && title.value !== 'Past Invitations') return true
  return false
})

/* ---------- fetch ---------- */
async function loadDetail() {
  loading.value = true
  try {
    const fd = new FormData()
    // API sample shows it needs: id (user id) and eventid
    fd.append('id', session.userId || '')
    fd.append('eventid', eventId.value || '')

    const res = await api.getReceivedInvitationDetail(fd).catch(() => null)
    const ok = res?.data?.status === 'ok' || res?.status === 'ok'
    if (ok) {
      const row = res.data?.list?.[0] || {}
      // normalize numerics once
      row.status = Number(row.status || 0)
      row.active_status = Number(row.active_status || 0)
      row.complete_status = Number(row.complete_status || 0)
      row.adult = Number(row.adult || 0)
      row.child = Number(row.child || 0)

      eventRow.value = row
      info.value = row

      // reset slider
      currentSlide.value = 0
      stopAuto()
      if (photosClean.value.length > 1) startAuto()
    } else {
      eventRow.value = {}
      info.value = {}
    }
  } finally {
    loading.value = false
  }
}

async function loadConnectionFilters() {
  // Build tab pills from server stats; fall back to defaults when unavailable
  if (!session.userId || !eventId.value) {
    connectionFilterOptions.value = [...defaultConnectionFilters]
    connectionFilter.value = defaultConnectionFilters[0].value
    return
  }
  try {
    const fd = new FormData()
    fd.append('userid', String(session.userId || ''))
    fd.append('event_id', String(eventId.value || ''))

    const res = await api.getConnectionsStats(fd).catch(() => null)
    const ok = res?.data?.status === 'ok'
    if (ok) {
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      const mapped = rows
        .map(item => ({
          value: item?.value != null ? String(item.value) : '',
          label: item?.name || item?.label || 'All'
        }))
        .filter(opt => opt.label)
      connectionFilterOptions.value = mapped.length ? mapped : [...defaultConnectionFilters]
    } else {
      connectionFilterOptions.value = [...defaultConnectionFilters]
    }
  } catch (err) {
    console.error('load connection stats error', err)
    connectionFilterOptions.value = [...defaultConnectionFilters]
  }
  const first = connectionFilterOptions.value[0]?.value ?? ''
  if (connectionFilter.value !== first) {
    connectionFilter.value = first
  }
}

async function loadConnections() {
  if (!session.userId || !eventId.value) {
    connections.value = []
    connectionsLoading.value = false
    return
  }
  connectionsLoading.value = true
  try {
    const fd = new FormData()
    fd.append('userid', String(session.userId || ''))
    fd.append('event_id', String(eventId.value || ''))
    fd.append('status', String(connectionFilter.value ?? ''))

    const res = await api.getConnections(fd).catch(() => null)
    const ok = res?.data?.status === 'ok'
    if (ok) {
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      connections.value = rows.map((row, idx) => ({
        id: row.id || row.userid || row.user_id || `guest-${idx}`,
        name: row.name || row.username || row.email || 'Connection',
        username: row.username || row.user_name || row.handle || '',
        email: row.email || '',
        phone: row.phone || '',
        user_img: row.user_img || row.photo || '',
        status: numberOrZero(row.status || row.response_status),
        rsvp_status: numberOrZero(row.rsvp_status || row.rsvpStatus),
        adult: numberOrZero(row.adult || row.adults),
        child: numberOrZero(row.child || row.children),
        mutual_friends: numberOrZero(row.mutual_friends || row.mutualFriends || row.mutual_count),
        mutual_events: numberOrZero(row.mutual_events || row.mutualEvents || row.events_with_you || row.events),
        last_seen: row.last_seen || row.lastSeen || row.last_interaction || '',
        past_count_msg: row.past_count_msg || row.pastCountMsg || '',
        source: row.source || row.connection_source || row.connected_via || '',
        response_note: row.response_note || row.responseNote || row.response_msg || '',
        note: row.note || row.notes || '',
        connection_user_id: row.userid || row.user_id || row.id
      }))
    } else {
      connections.value = []
    }
  } catch (err) {
    console.error('load connections error', err)
    connections.value = []
  } finally {
    connectionsLoading.value = false
  }
}

/* --------------------- RSVP modal state --------------------- */
const rsvpOpen   = ref(false)
const modalMode  = ref('new') // 'new' | 'update'
const modalId    = ref(null)  // invitation row id (list[0].id)
const modalAdult = ref(0)
const modalChild = ref(0)
const modalStatus= ref(1)     // 1 yes, 2 no, 3 may
const modalNotes = ref('')
const saving     = ref(false)

function statusLabel(s) {
  if (s === 1) return 'YES'
  if (s === 2) return 'NO'
  return 'MAY'
}
function badgeClass(s) {
  if (s === 1) return 'badge-success'
  if (s === 2) return 'badge-danger'
  return 'badge-primary'
}

function selectStatus(s) {
  modalStatus.value = Number(s)
  if (modalStatus.value === 2) {
    modalAdult.value = 0
    modalChild.value = 0
  }
}

const changeCount = (field, delta) => {
  if (!rsvpFamilyEnabled.value || modalStatus.value === 2) return
  const apply = (val) => Math.max(0, Number(val || 0) + delta)
  if (field === 'adult') modalAdult.value = apply(modalAdult.value)
  if (field === 'child') modalChild.value = apply(modalChild.value)
}

function openRsvpModal(row, mode, presetStatus) {
  if (!rsvpEnabled.value) return
  modalMode.value   = mode
  modalId.value     = Number(row.id || row.eventid || row.id)
  modalStatus.value = Number(presetStatus ?? row.status ?? 1)
  modalNotes.value  = row?.response_note || row?.note || row?.notes || ''

  modalAdult.value = rsvpFamilyEnabled.value ? Number(row.adult || 0) : 0
  modalChild.value = rsvpFamilyEnabled.value ? Number(row.child || 0) : 0
  if (modalStatus.value === 2) {
    modalAdult.value = 0
    modalChild.value = 0
  }
  rsvpOpen.value = true
}
function closeModal() {
  if (saving.value) return
  rsvpOpen.value = false
}

/* --------------------- Save to API + refresh/toast --------------------- */
async function saveRsvp() {
  if (!modalId.value) return
  try {
    saving.value = true

    const fd = new FormData()
    fd.append('id', String(modalId.value))                  // invitation row id
    fd.append('status', String(modalStatus.value))
    const a = (!rsvpFamilyEnabled.value || modalStatus.value === 2) ? 0 : Math.max(0, Number(modalAdult.value || 0))
    const c = (!rsvpFamilyEnabled.value || modalStatus.value === 2) ? 0 : Math.max(0, Number(modalChild.value || 0))
    fd.append('adult', String(a))
    fd.append('child', String(c))
    fd.append('notes', modalNotes.value || '')

    const res = await api.updateRSVPReceived(fd).catch(() => null)
    const ok = res?.data?.status === 'ok' || res?.data?.status === true || res?.status === true
    if (ok) {
      rsvpOpen.value = false
      // toast
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'RSVP updated',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      })
      // refresh UI from server
      await loadDetail()
      emit('rsvp-updated')
    } else {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: 'Failed to update RSVP',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      })
    }
  } finally {
    saving.value = false
  }
}

/* --------------------- Feedback modal (create/update) --------------------- */
const feedbackOpen = ref(false)
const feedbackId = ref(null)
const feedbackMsg = ref('')
const feedbackRating = ref('')
const feedbackLabels = ['Very poor', 'Poor', 'Average', 'Good', 'Excellant']
const savingFeedback = ref(false)
const feedbackError = ref('')
const feedbackEmojiSize = ref(54)
const placeholderImage = 'https://dummyimage.com/400x300/e9edf5/7c8697.png&text=Event'
const feedbackTarget = ref(null)
const showRatingPicker = computed(() => {
  const target = feedbackTarget.value || info.value
  return Number(target?.rating || target?.feedback_rating_flag) === 1
})

function openFeedback(row) {
  // row is info (the event object)
  feedbackTarget.value = row
  feedbackId.value = row?.feedback_id || row?.feedbackId || null
  feedbackMsg.value = row?.feedback_msg || row?.feedbackMsg || ''
  const existingRating = row?.feedback_rating || row?.rating
  feedbackRating.value = existingRating ? String(existingRating) : ''
  feedbackError.value = ''
  feedbackOpen.value = true
}

function closeFeedback() {
  if (savingFeedback.value) return
  feedbackOpen.value = false
  feedbackId.value = null
  feedbackMsg.value = ''
  feedbackRating.value = ''
  feedbackError.value = ''
  feedbackTarget.value = null
}

function goToConnectionsPage() {
  const slug = route.params?.slug || eventId.value || ''
  if (slug) {
    router.push({ name: 'received-connections', params: { slug } })
  } else {
    router.push({ name: 'received' })
  }
}

/* ---------- Connection events modal ---------- */
const connectionEventsOpen = ref(false)
const selectedConnection = ref(null)
const connectionEvents = ref([])
const connectionEventsLoading = ref(false)
const connectionEventsError = ref('')

async function openConnectionEvents(friend) {
  if (!friend) return
  selectedConnection.value = friend
  connectionEvents.value = []
  connectionEventsError.value = ''
  connectionEventsOpen.value = true
  await loadConnectionEvents(friend)
}

async function loadConnectionEvents(friend) {
  const targetUserId = friend?.connection_user_id || friend?.userid || friend?.user_id || friend?.id
  if (!targetUserId) {
    connectionEventsError.value = 'User id unavailable for this connection.'
    return
  }
  connectionEventsLoading.value = true
  try {
    const fd = new FormData()
    fd.append('userid', String(targetUserId))
    if (eventId.value) fd.append('event_id', String(eventId.value))

    const res = await api.getConnectionsEvents(fd).catch(() => null)
    const ok = res?.data?.status === 'ok'
    if (ok) {
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      connectionEvents.value = rows.map((row, idx) => ({
        id: row.id || row.event_id || row.eventid || `event-${idx}`,
        eventId: row.eventid || row.event_id || row.id || null,
        title: row.eventname || row.name || row.orgname || 'Event',
        dateRange: formatEventDateRange({
          fromDate: row.fromdate || row.date,
          toDate: row.todate || row.date,
          fromTime: row.fromtime || row.time,
          toTime: row.totime || row.time_to
        }),
        location: row.address || row.location || '',
        host: row.hostname || row.host || '',
        description: row.description || row.details || row.note || '',
        image: row.eventphoto || row.p1_url || row.photo || row.image || row.banner || row.cover || defaultEventImage
      }))
    } else {
      connectionEventsError.value = res?.data?.msg || 'Unable to load events.'
    }
  } catch (err) {
    console.error('get connections events error', err)
    connectionEventsError.value = 'Unable to load events.'
  } finally {
    connectionEventsLoading.value = false
  }
}

function closeConnectionEvents() {
  if (connectionEventsLoading.value) return
  connectionEventsOpen.value = false
  connectionEvents.value = []
  selectedConnection.value = null
  connectionEventsError.value = ''
}

async function submitFeedback() {
  // Use updateFeedback when feedbackId exists, otherwise saveFeedback
  try {
    savingFeedback.value = true

    if (showRatingPicker.value && !feedbackRating.value) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Pick a rating (1-5)',
        showConfirmButton: false,
        timer: 2000
      })
      return
    }

    const trimmedMsg = String(feedbackMsg.value || '').trim()
    if (!trimmedMsg) {
      feedbackError.value = 'Please enter your feedback.'
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Add feedback before saving',
        showConfirmButton: false,
        timer: 2000
      })
      return
    }
    feedbackError.value = ''

    let res = null

    if (feedbackId.value) {
      // update existing feedback: api.updateFeedback expects feedback_id and message
      if (typeof api.updateFeedback !== 'function') {
        Swal.fire({ icon: 'error', title: 'Error', text: 'updateFeedback API not available' })
        return
      }
      const fd = new FormData()
      fd.append('feedback_id', String(feedbackId.value))
      fd.append('message', trimmedMsg)
      fd.append('rating', String(feedbackRating.value))
      res = await api.updateFeedback(fd).catch(() => null)
    } else {
      // create new feedback: prefer api.saveFeedback (user_id, event_id, message)
      if (typeof api.saveFeedback !== 'function') {
        // fallback: if updateFeedback exists but user didn't provide id, we cannot update — show error
        if (typeof api.updateFeedback === 'function') {
          Swal.fire({ icon: 'error', title: 'Error', text: 'No feedback_id: cannot call updateFeedback' })
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: 'No feedback API available' })
        }
        return
      }
      const fd = new FormData()
      fd.append('user_id', String(session.userId || ''))
      fd.append('event_id', String(eventId.value || ''))
      fd.append('message', trimmedMsg)
      fd.append('rating', String(feedbackRating.value))
      res = await api.saveFeedback(fd).catch(() => null)
    }

    const ok = res?.data?.status === 'ok' || res?.status === true || res?.data?.status === true
    if (ok) {
      feedbackOpen.value = false
      feedbackError.value = ''
      await loadDetail()
      emit('rsvp-updated')
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Feedback saved',
        showConfirmButton: false,
        timer: 1600,
      })
    } else {
      Swal.fire({ icon: 'error', title: 'Failed', text: res?.data?.msg || 'Failed to save feedback' })
    }
  } catch (err) {
    console.error('feedback error', err)
    Swal.fire({ icon: 'error', title: 'Error', text: 'Unable to save feedback' })
  } finally {
    savingFeedback.value = false
  }
}

/* ---------- lifecycle ---------- */
onMounted(() => {
  loadDetail()
  loadConnectionFilters()
  updateEmojiSize()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateEmojiSize)
  }
})
watch(eventId, (val, oldVal) => {
  if (!val || val === oldVal) return
  loadDetail()
  loadConnectionFilters()
  loadConnections()
})
watch(() => session.userId, (val, oldVal) => {
  if (!val || val === oldVal) return
  loadConnectionFilters()
  loadConnections()
})
watch(connectionFilter, (val, oldVal) => {
  if (val === oldVal) return
  loadConnections()
}, { immediate: true })
onBeforeUnmount(() => {
  stopAuto()
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateEmojiSize)
  }
})

</script>

<style scoped>
/* Header */
.page-bar{
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.page-title{
  font-weight: 600;
  font-size: 1.05rem;
}

/* HERO / SLIDER */
.slide-frame{
  position: relative;
  width: 100%;
  overflow: hidden;
}
.slide-frame::before{
  content: "";
  display: block;
  padding-top: 56.25%;
}
.slide-bg-blur{
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: blur(20px) brightness(0.9);
  transform: scale(1.1);
}
.slide-img{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: .5rem;
  box-shadow: 0 2px 10px rgba(0,0,0,.08);
}
.slide-img.fit-contain{ object-fit: contain; background: transparent; }
.slide-img.fit-cover{ object-fit: cover; object-position: top center; }

.nav{
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: rgba(0,0,0,.35);
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display:flex;align-items:center;justify-content:center;
  cursor: pointer;
}
.nav.prev{ left: 10px; }
.nav.next{ right: 10px; }
.nav i{ font-size: 14px; }

.thumbs{ display: flex; flex-wrap: nowrap; overflow-x: auto; gap: .5rem; padding-bottom: .25rem; }
.thumb-btn{ padding: 0; border: 2px solid transparent; background: transparent; border-radius: .35rem; line-height: 0; flex: 0 0 auto; }
.thumb-btn.active{ border-color: #0d6efd; }
.thumb{ width: 88px; height: 56px; object-fit: cover; border-radius: .35rem; display: block; box-shadow: 0 1px 6px rgba(0,0,0,.08); }

/* Section titles */
.section-title{ font-weight: 600; font-size: 1rem; margin-bottom: .5rem; }

/* Icon rows */
.icon-row i{ color: #0d6efd; width: 22px; text-align: center; }
.emoji-icon{
  display:inline-block;
  line-height:1;
}

/* Small helpers */
.text-small{font-size:.9rem;}


/* --- Modal (center aligned, no Bootstrap JS required) --- */
.modal-overlay {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,.45);
  z-index: 2000;
  padding: 1rem;
}
.modal-card {
  width: min(520px, 92vw);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,.25);
  overflow: hidden;
  display: flex; flex-direction: column;
}
.modal-header, .modal-footer {
  padding: .85rem 1rem;
  border-bottom: 1px solid #f1f3f5;
}
.modal-footer { border-top: 1px solid #f1f3f5; border-bottom: 0; }
.modal-body {
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
}
.close-btn {
  background: transparent; border: 0; font-size: 1.25rem; line-height: 1;
  color: #6c757d; cursor: pointer;
}
.close-btn:hover { color: #000; }
.modal-avatar{
  width:42px;
  height:42px;
  border-radius:50%;
  object-fit:cover;
  border:2px solid #fff;
  box-shadow:0 6px 14px rgba(15,23,42,.15);
}

/* RSVP modal */
.rsvp-modal{
  width: min(560px, 96vw);
  border-radius: 18px;
}
.rsvp-body{
  display:flex;
  flex-direction:column;
  gap:1rem;
  padding:1.1rem;
}
.rsvp-meta{
  display:flex;
  flex-direction:column;
  gap:.2rem;
  align-items:center;
  text-align:center;
}
.rsvp-meta__name{
  font-weight:700;
  color:#0f172a;
}
.rsvp-meta__sub{
  font-size:.9rem;
  color:#6b7280;
}
.rsvp-choice-row{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap:.6rem;
}
.rsvp-chip{
  border:1px solid #e5e7eb;
  background:#f8fafc;
  color:#111827;
  border-radius:16px;
  padding:.75rem .8rem;
  font-weight:700;
  text-transform:capitalize;
  width:100%;
  min-width:0;
  transition:all .15s ease;
  box-shadow:0 6px 14px rgba(15,23,42,.06);
}
.rsvp-chip.active{
  color:#0f172a;
  border-color:transparent;
  box-shadow:0 12px 24px rgba(15,23,42,.1);
}
.rsvp-chip.yes.active{ background:#34d399; color:#0f172a; }
.rsvp-chip.no.active{ background:#e5e7eb; color:#374151; }
.rsvp-chip.maybe.active{ background:#dbeafe; color:#1d4ed8; }
.rsvp-grid{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap:.75rem;
}
.rsvp-counter{
  background:#f8fafc;
  border:1px solid #e5e7eb;
  border-radius:14px;
  padding:.75rem;
}
.rsvp-counter label{
  display:block;
  margin-bottom:.35rem;
  font-weight:700;
  color:#0f172a;
}
.rsvp-counter .form-control{
  border-radius:10px;
}
.stepper{
  display:grid;
  grid-template-columns: repeat(3, 1fr);
  border:1px solid #d7dce5;
  border-radius:12px;
  overflow:hidden;
  background:#fff;
}
.stepper.disabled{
  opacity:.65;
}
.step-btn{
  border:0;
  background:#f8fafc;
  color:#0f172a;
  font-weight:700;
  padding:.55rem 0;
  transition:background .12s ease;
}
.step-btn:first-child{
  border-right:1px solid #e5e7eb;
}
.step-btn:last-child{
  border-left:1px solid #e5e7eb;
}
.step-btn:active{
  background:#e5e7eb;
}
.step-value{
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:700;
  color:#111827;
  padding:.55rem 0;
}
.rsvp-note{
  font-size:.9rem;
  color:#6b7280;
  margin:0;
}
.rsvp-actions{
  display:flex;
  gap:.65rem;
  flex-wrap:wrap;
  justify-content:flex-end;
}
.rsvp-btn{
  min-width:120px;
  border-radius:12px;
  font-weight:700;
}
.rsvp-btn.primary{
  box-shadow:0 12px 24px rgba(37,99,235,.25);
}
@media (max-width: 640px){
  .modal-overlay{
    align-items:flex-end;
  }
  .modal-card{
    width:100%;
    border-radius:18px 18px 0 0;
  }
  .modal-body{
    max-height:65vh;
  }
}

/* Feedback scale */
.feedback-scale {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.feedback-scale .vue-feedback-reaction {
  margin: 0;
}
.selected-rating {
  font-weight: 600;
  color: #0f172a;
}
.feedback-summary{
  display:flex;
  align-items:center;
  gap:.5rem;
  flex-wrap:wrap;
}
.feedback-summary__text{
  color:#4b5563;
  font-size:.95rem;
}
.feedback-chip{
  display:inline-flex;
  align-items:center;
  gap:.25rem;
  background:#fef3c7;
  color:#92400e;
  border-radius:999px;
  padding:.3rem .7rem;
  font-weight:700;
  font-size:.9rem;
}
@media (max-width: 576px){
  .feedback-scale{
    width:100%;
  }
  .feedback-chip{
    font-size:.8rem;
  }
  .feedback-summary__text{
    font-size:.9rem;
  }
}

/* Badges */
.badge { display:inline-block; padding:.4rem .6rem; border-radius:.35rem; font-weight:600; font-size:.9rem; }
.badge-success{ background:#d1e7dd; color:#0f5132; border:1px solid #badbcc; }
.badge-danger{  background:#f8d7da; color:#842029; border:1px solid #f5c2c7; }
.badge-primary{ background:#cfe2ff; color:#084298; border:1px solid #b6d4fe; }
.badge-secondary{ background:#e2e3e5; color:#41464b; border:1px solid #d3d6d8; }

.map-card{
  border:1px solid #e5e7eb;
  border-radius:12px;
  overflow:hidden;
  box-shadow:0 8px 20px rgba(15,23,42,.08);
  background:#fff;
}
.map-card__header{
  padding:.75rem 1rem;
  font-weight:600;
  color:#0f172a;
  border-bottom:1px solid #f3f4f6;
  font-size:.95rem;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:.5rem;
}
.map-card__header a{
  font-size:.85rem;
  color:#0d6efd;
  font-weight:500;
}
.map-frame{
  position:relative;
  padding-top:56.25%;
  background:#f1f5f9;
}
.map-frame__iframe{
  position:absolute;
  inset:0;
  width:100%;
  height:100%;
  border:0;
}

/* Connections */
.connections-panel{
  position:relative;
  padding:.25rem 0;
}
.connections-panel-body{
  position:relative;
}
.connection-filters{
  display:flex;
  flex-wrap:wrap;
  gap:.25rem;
  margin-bottom:.5rem;
  overflow-x:auto;
  padding-bottom:.2rem;
  scrollbar-width: thin;
}
.connection-filter-chip{
  border:1px solid #e3e7f2;
  border-radius:999px;
  background:#fff;
  color:#4b5563;
  font-size:.78rem;
  font-weight:600;
  padding:.25rem .85rem;
  line-height:1.2;
  transition:all .15s ease;
}
.connection-filter-chip.active{
  background:#f1edff;
  color:#5435d6;
  border-color:#8b5cf6;
  box-shadow:0 4px 12px rgba(139,92,246,.25);
}
.connection-filter-empty{
  padding:1rem 0;
  display:flex;
  justify-content:center;
}
.connection-filter-empty img{
  max-width:70%;
  width:140px;
  opacity:.65;
}
.connection-list{
  display:flex;
  flex-direction:column;
  gap:.6rem;
}
.connection-item{
  display:flex;
  align-items:center;
  gap:.75rem;
  padding:1rem 1.1rem;
  border-radius:14px;
  border:1px solid #e6e9f2;
  background:#fff;
  box-shadow:none;
}
.connection-item--clickable{
  cursor:pointer;
  transition: transform .1s ease, box-shadow .1s ease;
}
.connection-item--clickable:hover{
  transform: translateY(-1px);
  box-shadow:0 6px 14px rgba(15,23,42,.1);
}
.connection-item.is-skeleton{
  box-shadow:none;
  background:#fff;
  border-color:#eef1f6;
}
.connection-avatar-wrap{
  position:relative;
  flex-shrink:0;
}
.connection-avatar{
  width:34px;
  height:34px;
  border-radius:50%;
  object-fit:cover;
  background:#f3f4f6;
  border:2px solid #fff;
  box-shadow:none;
}
.connection-status-dot{
  position:absolute;
  width:12px;
  height:12px;
  border-radius:50%;
  border:2px solid #fff;
  right:2px;
  bottom:0;
  background:#e5e7eb;
}
.connection-body{
  flex:1;
  min-width:0;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:.75rem;
}
.connection-name{
  font-size:12px;
  font-weight:700;
  color:#111827;
  text-transform:capitalize;
  word-break:break-word;
}
.connection-main{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:.3rem;
}
.connection-rsvp{
  display:flex;
  align-items:center;
  gap:.35rem;
  font-size:14px;
  color:#4b5563;
}
.connection-rsvp-text{
  font-weight:600;
}
.connection-chip{
  border-radius:999px;
  padding:5px 12px;
  font-size:10px;
  font-weight:600;
  text-transform:capitalize;
  background:#f3f4f6;
  color:#0f172a;
  white-space:nowrap;
  align-self:flex-start;
}
.connection-past-pill{
  display:inline-flex;
  align-items:center;
  gap:.25rem;
  padding:0;
  border-radius:0;
  background:transparent;
  color:#4338ca;
  font-weight:400;
  font-size:10px;
}
.connection-meta-dot.is-going,
.connection-status-dot.is-going{
  background:#16a34a;
}
.connection-chip.is-going{
  background:#d1fae5;
  color:#047857;
}
.connection-meta-dot.is-maybe,
.connection-status-dot.is-maybe{
  background:#fbbf24;
}
.connection-chip.is-maybe{
  background:#fef3c7;
  color:#92400e;
}
.connection-meta-dot.is-notgoing,
.connection-status-dot.is-notgoing{
  background:#f87171;
}
.connection-chip.is-notgoing{
  background:#fee2e2;
  color:#b91c1c;
}
.connection-meta-dot.is-pending,
.connection-status-dot.is-pending{
  background:#a78bfa;
}
.connection-chip.is-pending{
  background:#ede9fe;
  color:#5b21b6;
}

.connections-overlay{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  background:rgba(255,255,255,.78);
  pointer-events:none;
  animation: fadeIn .18s ease;
}
.loading-bar{
  width:80%;
  max-width:240px;
  height:6px;
  border-radius:999px;
  background:linear-gradient(90deg, #e5e7eb, #cbd5f5, #e5e7eb);
  background-size:200% 100%;
  animation: loadingBar 1.1s linear infinite;
}

.event-card{
  border:1px solid #edf1f7;
  border-radius:14px;
  padding:.85rem;
  box-shadow:0 6px 16px rgba(15,23,42,.06);
  background:#fdfdff;
  margin-bottom:.75rem;
}
.event-card__media{
  width:100%;
  overflow:hidden;
  border-radius:12px;
  margin-bottom:.65rem;
}
.event-img{
  width:100%;
  height:180px;
  object-fit:cover;
  display:block;
  border-radius:12px;
}
.event-card__body{
  display:flex;
  flex-direction:column;
  gap:.2rem;
}
.event-title{
  font-weight:700;
  color:#0f172a;
}
.event-meta i{
  color:#6366f1;
}

.skeleton-box{
  position:relative;
  overflow:hidden;
  background:#e9edf7;
  border-radius:10px;
}
.skeleton-line{
  height:12px;
}
.skeleton-box::after{
  content:"";
  position:absolute;
  inset:0;
  background:linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.7), rgba(255,255,255,0));
  animation: shimmer 1.2s infinite;
}
.w-30{width:30%;}
.w-40{width:40%;}
.w-60{width:60%;}
.w-90{width:90%;}
.mt-1{margin-top:.25rem;}
.mt-2{margin-top:.5rem;}

@keyframes shimmer{
  from{transform:translateX(-100%);}
  to{transform:translateX(100%);}
}
@keyframes loadingBar{
  from{background-position:200% 0;}
  to{background-position:-200% 0;}
}
@keyframes fadeIn{
  from{opacity:0;}
  to{opacity:1;}
}

@media (max-width: 768px){
  .connection-filters{
    flex-wrap:nowrap;
    white-space:nowrap;
  }
}

@media (max-width: 576px){
  .connection-item{
    padding:.9rem .95rem;
    gap:.6rem;
  }
  .connection-avatar{
    width:28px;
    height:28px;
  }
  .connection-name{
    font-size:12px;
  }
  .connection-chip{
    font-size:12px;
    padding:5px 10px;
  }
  .connection-past-pill{
    font-size:10px;
  }
}
</style>
